/**
 * Every roll Learn to Play (`components/LearnToPlay.tsx`) shows, with what it is meant to kill, so
 * `learnRolls.test.ts` can check each one against the game's rules (`canKill`, `canModifyDie`).
 * The table: you are the Knight of 3's (Kill Quest `p3`, power `w3`); the others are the 3 of a
 * Kind (`xxx`), Ka'Dice (`kadice`) and 4 of a Kind (`xxxx`) cards.
 */

/** The Kill Quests on round 1's table: yours, then the other three players'. */
export const TABLE_KILLERS = ['p3', 'xxx', 'kadice', 'xxxx'] as const;

/** Rolls that kill nothing on round 1's table, per player (A, B, C, you), one per turn. */
export const SAFE = {
  a: [
    [2, 6, 1, 5, 6],
    [4, 4, 1, 6, 2],
    [5, 6, 2, 2, 1],
  ],
  b: [
    [1, 4, 6, 6, 2],
    [6, 5, 5, 1, 2],
    [2, 2, 6, 4, 1],
  ],
  c: [
    [5, 1, 2, 2, 6],
    [6, 1, 4, 4, 2],
    [1, 6, 6, 5, 2],
  ],
  you: [
    [2, 6, 4, 1, 6],
    [5, 5, 1, 6, 2],
    [6, 2, 2, 1, 4],
  ],
} as const;

/**
 * The turns of the order and round steps, where every player saves dice: the dice each holds at the
 * end of turn 1 and turn 2 (cumulative, the last turn holds nothing).
 */
export const HOLDS = {
  a: [[0, 3], [0, 3], []],
  b: [[1, 3], [1, 3], []],
  c: [[3], [3, 4], []],
  you: [[1, 2], [1, 2, 0], []],
} as const satisfies Record<keyof typeof SAFE, readonly (readonly number[])[]>;

/** SAFE rolls with the dice held the turn before kept in place: what is shown in those steps. */
export const SAVED: Record<keyof typeof SAFE, number[][]> = Object.fromEntries(
  (Object.keys(SAFE) as (keyof typeof SAFE)[]).map((who) => {
    const rolls: number[][] = [];
    SAFE[who].forEach((safe, turn) => {
      const kept: readonly number[] = turn > 0 ? HOLDS[who][turn - 1]! : [];
      rolls.push(safe.map((v, i) => (kept.includes(i) ? rolls[turn - 1]![i]! : v)));
    });
    return [who, rolls];
  }),
) as Record<keyof typeof SAFE, number[][]>;

/**
 * A scripted roll: the dice, the ones held for the kill (`keep`), the Kill Quests the held dice
 * must meet (`kills`) and must not (`spares`). `changes`: dice moved by the Knight's power first
 * (index, from, to).
 */
export interface Roll {
  dice: readonly number[];
  keep: readonly number[];
  kills: readonly string[];
  spares: readonly string[];
  changes?: readonly (readonly [number, number, number])[];
}

export const ROLLS = {
  /** Vulnerabilities: they roll your pair of 3s... */
  theyKillYou: { dice: [3, 5, 3, 1, 6], keep: [0, 2], kills: ['p3'], spares: [] },
  /** ...then you roll their 3 of a kind. */
  youKillThem: { dice: [4, 4, 2, 4, 6], keep: [0, 1, 3], kills: ['xxx'], spares: ['xxxx', 'kadice'] },
  /** Powers: the wild 3s become 4s, three 4s. */
  powerThree: {
    dice: [1, 3, 3, 4, 6],
    changes: [
      [1, 3, 4],
      [2, 3, 4],
    ],
    keep: [1, 2, 3],
    kills: ['xxx'],
    spares: ['xxxx'],
  },
  /** The same roll, the 3s made a 2 and a 5: a Ka'Dice. */
  powerKadice: {
    dice: [1, 3, 3, 4, 6],
    changes: [
      [1, 3, 2],
      [2, 3, 5],
    ],
    keep: [0, 1, 2, 3, 4],
    kills: ['kadice'],
    spares: ['xxx'],
  },
  /** First turn: five dice, nothing to kill. */
  first: { dice: SAFE.you[0], keep: [], kills: [], spares: ['p3', 'xxx', 'kadice', 'xxxx'] },
  /** Four 5s: the 3 of a Kind and 4 of a Kind cards at once. */
  doubleKill: { dice: [5, 2, 5, 5, 5], keep: [0, 2, 3, 4], kills: ['xxx', 'xxxx'], spares: ['kadice'] },
  /** Save dice: nothing to kill, two 4s held... */
  saveFirst: { dice: [6, 4, 1, 4, 2], keep: [1, 3], kills: [], spares: ['p3', 'xxx', 'kadice', 'xxxx'] },
  /** ...the other three rolled next turn: three 4s. */
  saveSecond: { dice: [4, 4, 6, 4, 2], keep: [0, 1, 3], kills: ['xxx'], spares: ['xxxx', 'kadice'] },
  /** Greed: three 6s could kill the 3 of a Kind card now; held for the 4 of a Kind too... */
  greedYou: { dice: [6, 2, 6, 6, 5], keep: [0, 2, 3], kills: ['xxx'], spares: ['xxxx', 'kadice'] },
  /** ...but the next player rolls your pair of 3s. */
  greedThem: { dice: [3, 3, 5, 1, 2], keep: [0, 1], kills: ['p3'], spares: [] },
  /** The win: three 4s take the last card. */
  win: { dice: [4, 4, 1, 4, 6], keep: [0, 1, 3], kills: ['xxx'], spares: [] },
} as const satisfies Record<string, Roll>;

/** The war: one die each, the higher wins. */
export const WAR_ROLL: readonly [readonly number[], readonly number[]] = [[5], [2]];
/** The tied war: tie, tie, then a win, one more die each time. */
export const WAR_TIES: readonly (readonly [readonly number[], readonly number[]])[] = [
  [[4], [4]],
  [
    [3, 5],
    [6, 2],
  ],
  [
    [6, 5, 2],
    [1, 4, 3],
  ],
];

/** The dice after the roll's power changes. */
export const changed = (roll: Roll): number[] =>
  roll.dice.map((v, i) => roll.changes?.find(([at]) => at === i)?.[2] ?? v);
